import { updateUserPermission } from '@app/modules/admin/api/permissions';
import { StatusSwitch } from '@app/modules/admin/components/permission/StatusSwitch';
import {
  canUserTogglePermission,
  getGroupFromPermission,
} from '@app/modules/admin/screens/stations/helpers';
import { StationLabel } from '@app/modules/admin/screens/stations/list/StationLabel';
import {
  AdminIndicator,
  PermissionInheritedIndicator,
} from '@app/modules/admin/screens/stations/users-permissions/components';
import { UserPermissionForStationState, UserPermissionSource } from '@app/modules/admin/types';
import { User, UserRole } from '@mi-front/core';
import { useToast } from '@app/core/notifications';
import { Box, Card, CardContent } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { reportError } from '../../../../core/integrations/bugsnag';

interface Props {
  readonly permissionState: UserPermissionForStationState;
  readonly user: User;
  readonly onUpdate?: (updatedState: UserPermissionForStationState) => void;
}

export function UserStationPermissionItem(p: Props) {
  const { permissionState: initialPermissionState, user, onUpdate } = p;
  const [permissionState, setPermissionState] = useState(initialPermissionState);
  const [isUpdating, setIsUpdating] = useState(false);
  const { $t } = useIntl();
  const { notifyError } = useToast();

  const canModify = canUserTogglePermission(permissionState);
  const group = getGroupFromPermission(permissionState.permission);
  const hasPermission = permissionState.permission !== null;

  const handleToggle = async (enabled: boolean) => {
    if (!canModify || isUpdating) {
      return;
    }

    setIsUpdating(true);
    try {
      await updateUserPermission(permissionState.station.id, {
        userId: user.id,
        allowed: enabled,
      });

      const updatedState: UserPermissionForStationState = {
        ...permissionState,
        permission: enabled
          ? {
              type: UserPermissionSource.DIRECT,
            }
          : null,
      };

      setPermissionState(updatedState);
      onUpdate?.(updatedState);
    } catch (err) {
      reportError(err as Error);
      notifyError(
        $t({
          id: 'admin.users.viewScreen.permissions.updateError',
          defaultMessage: 'No se pudo actualizar el permiso',
        }),
      );
      // On error, revert to original state
      setPermissionState(initialPermissionState);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <StatusSwitch
            checked={hasPermission}
            onChange={(event) => {
              handleToggle(event.target.checked);
            }}
            disabled={!canModify || isUpdating}
            sx={{ marginBottom: 0 }}
          />
          <StationLabel station={permissionState.station} />
          {group && <PermissionInheritedIndicator group={group} />}
          {user.role === UserRole.CLIENT_ADMIN && <AdminIndicator />}
        </Box>
      </CardContent>
    </Card>
  );
}
